import React from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { IndicatorSectionKey } from './utils';

const defaultSingularBeneficiary = 'A person';
const getSingularBeneficiary = {
  Beneficiaries: 'A Beneficiary',
  Clients: 'A Client',
  'Program participants': 'A Program participant',
  Students: 'A Student',
  Youth: 'A Youth',
  'Low-income workers': 'A Low-income worker',
  'At-Risk Youth': 'An At-Risk Youth',
  'Community members': 'A Community member',
  'Underserved people': 'An Underserved person',
  Families: 'A Family',
};

interface SupportTextProps {
  programName?: string;
  isReportPassed: boolean;
  beneficiaryTerm?: string;
  sectionKey: IndicatorSectionKey;
  className?: string;
}

export const ImpactSummarySupportText: React.FC<SupportTextProps> = ({
  isReportPassed,
  sectionKey,
  className,
}) => {
  switch (sectionKey) {
    case 'capacityDevelopment':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-page" className={className}>
            Let's help your donors understand your capacity development
            metric(s).
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-page" className={className}>
          Let's help your donors understand your capacity development metric(s).
        </TrueImpactText>
      );
    case 'reach':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-page" className={className}>
            Let's help your donors understand your reach metric(s).
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-page" className={className}>
          Let's help your donors understand your reach metric(s).
        </TrueImpactText>
      );
    case 'learn':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-page" className={className}>
            Let's help your donors understand your learn metric.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-page" className={className}>
          Let's help your donors understand your learn metric.
        </TrueImpactText>
      );
    case 'act':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-page" className={className}>
            Let's help your donors understand your act metric.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-page" className={className}>
          Let's help your donors understand your act metric.
        </TrueImpactText>
      );
    case 'succeed':
      if (isReportPassed) {
        return (
          <TrueImpactText variant="heading-page" className={className}>
            Let's help your donors understand your succeed metric(s).
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText variant="heading-page" className={className}>
          Let's help your donors understand your succeed metric(s).
        </TrueImpactText>
      );
    default:
      return null;
  }
};

export const ForecastSupportText: React.FC<SupportTextProps> = ({
  isReportPassed,
  sectionKey,
  className,
}) => {
  switch (sectionKey) {
    case 'capacityDevelopment':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Share the total number that were realized during this reporting
            period.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Share the total number that will be realized during this reporting
          period.
        </TrueImpactText>
      );

    case 'reach':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Share the total number that were realized during this reporting
            period.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Share the total number that will be realized during this reporting
          period.
        </TrueImpactText>
      );

    case 'learn':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Share the total number that were realized because of the programming
            that happened during this reporting period.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Share the total number that will be realized because of the
          programming happening during this reporting period.
        </TrueImpactText>
      );

    case 'act':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Share the total number that were realized because of the programming
            that happened during this reporting period.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Share the total number that will be realized because of the
          programming happening during this reporting period.
        </TrueImpactText>
      );

    case 'succeed':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Share the total number that were realized because of the programming
            that happened during this reporting period.
            <br />
            <br />
            We understand that not all impacts are always fully realized during
            the reporting period. Stay tuned: we will ask you about that
            shortly.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Share the total number that will be realized because of the
          programming happening during this reporting period.
          <br />
          <br />
          We understand that not all impacts are always fully realized during
          the reporting period. Stay tuned: we will ask you about that shortly.
        </TrueImpactText>
      );

    default:
      return <></>;
  }
};

export const MeasurementTypeSupportText: React.FC<SupportTextProps> = ({
  isReportPassed,
  sectionKey,
  className,
}) => {
  switch (sectionKey) {
    case 'capacityDevelopment':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Ensure your measurements type matches how you generated data for
            this indicator. As a tip, if you collected your own data to generate
            this number, select directly measured. If you are calculating this
            number using existing research or other evidence, select estimate.
            If you are basing this number on informed speculation or anecdotal
            evidence, select guess.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Remember: we're asking about what you will achieve during the
          reporting period you've indicated. We understand that these are
          estimates right now, but please indicate how you will know this data
          at the end of the reporting period.
        </TrueImpactText>
      );

    case 'reach':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Ensure your measurements type matches how you generated data for
            this indicator. As a tip, if you collected your own data to generate
            this number, select directly measured. If you are calculating this
            number using existing research or other evidence, select estimate.
            If you are basing this number on informed speculation or anecdotal
            evidence, select guess.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Remember: we're asking about what you will achieve during the
          reporting period you've indicated. We understand that these are
          estimates right now, but please indicate how you will know this data
          at the end of the reporting period.
        </TrueImpactText>
      );

    case 'learn':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Ensure your measurements type matches how you generated data for
            this indicator. As a tip, if you collected your own data to generate
            this number, select directly measured. If you are calculating this
            number using existing research or other evidence, select estimate.
            If you are basing this number on informed speculation or anecdotal
            evidence, select guess.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Remember: we're asking about what you will achieve during the
          reporting period you've indicated. We understand that these are
          estimates right now, but please indicate how you will know this data
          at the end of the reporting period.
        </TrueImpactText>
      );

    case 'act':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Ensure your measurements type matches how you generated data for
            this indicator. As a tip, if you collected your own data to generate
            this number, select directly measured. If you are calculating this
            number using existing research or other evidence, select estimate.
            If you are basing this number on informed speculation or anecdotal
            evidence, select guess.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Remember: we're asking about what you will achieve during the
          reporting period you've indicated. We understand that these are
          estimates right now, but please indicate how you will know this data
          at the end of the reporting period.
        </TrueImpactText>
      );

    case 'succeed':
      if (isReportPassed) {
        return (
          <TrueImpactText className={className}>
            Ensure your measurements type matches how you generated data for
            this indicator. As a tip, if you collected your own data to generate
            this number, select directly measured. If you are calculating this
            number using existing research or other evidence, select estimate.
            If you are basing this number on informed speculation or anecdotal
            evidence, select guess.
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText className={className}>
          Remember: we're asking about what you will achieve during the
          reporting period you've indicated. We understand that these are
          estimates right now, but please indicate how you will know this data
          at the end of the reporting period.
        </TrueImpactText>
      );

    default:
      return <></>;
  }
};
